import { cva } from '@adverity/ds-tokens/cva'

export const buttonVariants = cva(
    [
        'inline-flex w-fit items-center justify-center shrink-0 whitespace-nowrap no-underline',
        'font-regular leading-none select-none cursor-pointer',
        'transition-colors duration-fast ease-in-out',
        'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/20',
        '[&_svg]:pointer-events-none',
    ],
    {
        variants: {
            variant: {
                primary: 'border border-transparent text-background',
                secondary: 'border bg-transparent',
                minimal: 'border border-transparent bg-transparent',
            },
            color: {
                brand: '',
                danger: '',
                success: '',
                warning: '',
                dimension: '',
                metric: '',
                neutral: '',
            },
            size: {
                small: 'h-medium min-w-[60px] gap-xx-small rounded-medium px-x-small text-x-small',
                medium: 'h-large min-w-[80px] gap-xx-small rounded-medium px-small text-small',
                large: 'h-x-large min-w-[100px] gap-xx-small rounded-medium px-small text-medium',
            },
            iconOnly: {
                true: '',
                false: '',
            },
        },
        compoundVariants: [
            // Primary + color
            {
                variant: 'primary',
                color: 'brand',
                className: 'bg-brand hover:bg-brand-highlighted active:bg-brand',
            },
            {
                variant: 'primary',
                color: 'danger',
                className: 'bg-danger-active hover:bg-danger-secondary-active active:bg-danger',
            },
            {
                variant: 'primary',
                color: 'success',
                className: 'bg-success-highlighted hover:bg-success-active active:bg-success',
            },
            {
                variant: 'primary',
                color: 'warning',
                className: 'bg-warning-highlighted hover:bg-warning-active active:bg-warning',
            },
            {
                variant: 'primary',
                color: 'dimension',
                className: 'bg-dimension-highlighted hover:bg-dimension-active active:bg-dimension',
            },
            {
                variant: 'primary',
                color: 'metric',
                className: 'bg-metric-highlighted hover:bg-metric-active active:bg-metric',
            },

            // Secondary + color
            {
                variant: 'secondary',
                color: 'brand',
                className:
                    'border-brand text-brand hover:bg-brand-secondary-highlighted active:bg-brand-secondary-active',
            },
            {
                variant: 'secondary',
                color: 'danger',
                className:
                    'border-danger-active text-danger-active hover:bg-danger-secondary active:bg-danger-secondary-highlighted',
            },
            {
                variant: 'secondary',
                color: 'success',
                className:
                    'border-success text-success hover:bg-success-secondary-highlighted active:bg-success-secondary-active',
            },
            {
                variant: 'secondary',
                color: 'warning',
                className:
                    'border-warning text-warning hover:bg-warning-secondary-highlighted active:bg-warning-secondary-active',
            },
            {
                variant: 'secondary',
                color: 'dimension',
                className:
                    'border-dimension text-dimension hover:bg-dimension-secondary-highlighted active:bg-dimension-secondary-active',
            },
            {
                variant: 'secondary',
                color: 'metric',
                className:
                    'border-metric text-metric hover:bg-metric-secondary-highlighted active:bg-metric-secondary-active',
            },

            // Minimal + color
            {
                variant: 'minimal',
                color: 'brand',
                className: 'text-brand hover:bg-brand-secondary-highlighted active:bg-brand-secondary-active',
            },
            {
                variant: 'minimal',
                color: 'danger',
                className: 'text-danger-active hover:bg-danger-secondary active:bg-danger-secondary-highlighted',
            },
            {
                variant: 'minimal',
                color: 'success',
                className: 'text-success hover:bg-success-secondary-highlighted active:bg-success-secondary-active',
            },
            {
                variant: 'minimal',
                color: 'warning',
                className: 'text-warning hover:bg-warning-secondary-highlighted active:bg-warning-secondary-active',
            },
            {
                variant: 'minimal',
                color: 'dimension',
                className:
                    'text-dimension hover:bg-dimension-secondary-highlighted active:bg-dimension-secondary-active',
            },
            {
                variant: 'minimal',
                color: 'metric',
                className: 'text-metric hover:bg-metric-secondary-highlighted active:bg-metric-secondary-active',
            },
            {
                variant: 'minimal',
                color: 'neutral',
                className: 'text-text hover:bg-background-emphasis active:bg-background-emphasis',
            },
            {
                variant: 'secondary',
                color: 'neutral',
                className: 'border-text text-text hover:bg-background-emphasis active:bg-background-emphasis',
            },
            {
                variant: 'primary',
                color: 'neutral',
                className: 'bg-text hover:bg-text-secondary active:bg-text',
            },

            // Focus ring color per variant color
            { color: 'danger', className: 'focus-visible:ring-danger/20' },
            { color: 'success', className: 'focus-visible:ring-success/20' },
            { color: 'warning', className: 'focus-visible:ring-warning/20' },
            { color: 'dimension', className: 'focus-visible:ring-dimension/20' },
            { color: 'metric', className: 'focus-visible:ring-metric/20' },
            { color: 'neutral', className: 'focus-visible:ring-text/20' },

            // Icon sizing (overrides ds-icon's --ds-icon-size custom property)
            // Text buttons: small/medium → 16px, large → 24px (default)
            { iconOnly: false, size: 'small', className: '[--ds-icon-size:16px]' },
            { iconOnly: false, size: 'medium', className: '[--ds-icon-size:16px]' },
            // Icon-only: small → 16px, medium/large → 24px (default)

            // Icon-only size overrides (square, no padding, no min-width)
            { iconOnly: true, size: 'small', className: 'w-medium min-w-[unset] px-no-space [--ds-icon-size:16px]' },
            { iconOnly: true, size: 'medium', className: 'w-large min-w-[unset] px-no-space' },
            { iconOnly: true, size: 'large', className: 'w-x-large min-w-[unset] px-no-space' },
        ],
        defaultVariants: {
            variant: 'primary',
            color: 'brand',
            size: 'medium',
            iconOnly: false,
        },
    },
)

export type ButtonVariant = 'primary' | 'secondary' | 'minimal'
export type ButtonColor = 'brand' | 'danger' | 'success' | 'warning' | 'dimension' | 'metric' | 'neutral'
export type ButtonSize = 'small' | 'medium' | 'large'
